import { logger } from 'module/logger';
import { SignalOccurrencesAPI } from 'api/signal-occurrences';
import { SignalSummaryValue } from 'api/signal-occurrences/types';
import moment from 'moment';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  GroupByType,
  getPeriodKeyByOffset,
  getDateRangeForPeriod,
  getPeriodStart,
  addPeriods,
  mergePeriodData,
  findPeriodIndex,
  getPeriodKeyFromItem,
} from './utils';

const ITEMS_PER_BATCH = 1;
const ITEMS_ABOVE = 3;
const PERIODS_BEFORE_AFTER = 2;

interface UseSummaryDataProps {
  companyId: number;
  groupBy: GroupByType;
  billingStartDate?: string;
}

export const useSummaryData = ({
  companyId,
  groupBy,
  billingStartDate = '2020-04-01',
}: UseSummaryDataProps) => {
  const [allData, setAllData] = useState<SignalSummaryValue[]>([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isLoadingSelectedPeriod, setIsLoadingSelectedPeriod] =
    useState<boolean>(false);
  const [isLoadingDown, setIsLoadingDown] = useState<boolean>(false);
  const [isLoadingUp, setIsLoadingUp] = useState<boolean>(false);
  const [loadedPeriods, setLoadedPeriods] = useState<Set<string>>(new Set());
  const fetchingPeriodsRef = useRef<Set<string>>(new Set());

  // Get current period key based on groupBy
  const currentSystemPeriod = useMemo(() => {
    return getPeriodKeyByOffset(0, groupBy);
  }, [groupBy]);

  // temporary fix for current month
  // Helper to get date range with special handling for current month
  const getDateRangeForPeriodWithCurrentMonth = useCallback(
    (periodKey: string, groupBy: GroupByType) => {
      const dateRange = getDateRangeForPeriod(periodKey, groupBy);

      // For current month, use end of month instead of today
      if (groupBy === 'MONTH' && periodKey === currentSystemPeriod) {
        const periodDate = moment(periodKey, 'YYYY-MM');
        const endOfMonth = periodDate.clone().endOf('month');

        return {
          startDate: dateRange.startDate,
          endDate: endOfMonth.format('YYYY-MM-DD'),
        };
      }

      return dateRange;
    },
    [currentSystemPeriod],
  );

  // Reset data when groupBy changes
  useEffect(() => {
    setAllData([]);
    setLoadedPeriods(new Set());
    setSelectedPeriodKey('');
    fetchingPeriodsRef.current.clear();
    setIsInitializing(true);
    setHasInitialized(false);
  }, [groupBy]);

  // Initialize with current period and preload surrounding periods
  useEffect(() => {
    const initializeData = async () => {
      if (allData.length > 0 || hasInitialized) {
        setIsInitializing(false);

        return;
      }

      setIsInitializing(true);
      const currentPeriod = currentSystemPeriod;

      setSelectedPeriodKey(currentPeriod);

      try {
        const dateRange = getDateRangeForPeriodWithCurrentMonth(
          currentPeriod,
          groupBy,
        );

        logger.info('Initializing data for current period', {
          period: currentPeriod,
          dateRange,
          groupBy,
          companyId,
        });

        const response = await SignalOccurrencesAPI.getSummary(companyId, {
          ...dateRange,
          groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
        });

        const valuesWithPeriodKeys = (response.values || []).map((item) => ({
          item,
          periodKey: currentPeriod,
        }));

        const { merged, periodKeys } = mergePeriodData(
          [],
          valuesWithPeriodKeys,
          groupBy,
        );

        setAllData(merged);
        setLoadedPeriods(periodKeys);

        // Preload surrounding periods
        const periodsToPreload: string[] = [];

        for (let i = 1; i <= ITEMS_ABOVE; i++) {
          const periodKey = getPeriodKeyByOffset(i, groupBy);
          const lowerCapDate = moment(billingStartDate);
          let lowerCapPeriod: string;

          switch (groupBy) {
            case 'WEEK':
              lowerCapPeriod = lowerCapDate.format('YYYY-[W]WW');
              break;
            case 'MONTH':
            default:
              lowerCapPeriod = lowerCapDate.format('YYYY-MM');
              break;
          }

          const periodDate = getPeriodStart(periodKey, groupBy);
          const lowerCapPeriodDate = getPeriodStart(lowerCapPeriod, groupBy);

          if (periodDate.isSameOrAfter(lowerCapPeriodDate)) {
            periodsToPreload.push(periodKey);
          }
        }

        const preloadPromises = periodsToPreload.map(async (periodKey) => {
          try {
            const periodDateRange = getDateRangeForPeriodWithCurrentMonth(
              periodKey,
              groupBy,
            );

            const preloadResponse = await SignalOccurrencesAPI.getSummary(
              companyId,
              {
                ...periodDateRange,
                groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
              },
            );

            return (preloadResponse.values || []).map((item) => ({
              item,
              periodKey,
            }));
          } catch (error) {
            logger.error('Error preloading period', { periodKey, error });

            return [];
          }
        });

        const preloadResults = await Promise.all(preloadPromises);
        const allPreloadData = preloadResults.flat();

        if (allPreloadData.length > 0) {
          const { merged: finalMerged, periodKeys: finalPeriodKeys } =
            mergePeriodData(merged, allPreloadData, groupBy);

          setAllData(finalMerged);
          setLoadedPeriods(finalPeriodKeys);
        }
      } catch (error) {
        logger.error('Error initializing data', { error, groupBy, companyId });
        setAllData([]);
        setLoadedPeriods(new Set());
      } finally {
        setIsInitializing(false);
        setHasInitialized(true);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, groupBy]);

  // Handle period selection from TOC
  const handlePeriodSelect = useCallback(
    async (periodKey: string) => {
      if (periodKey === selectedPeriodKey) {
        return;
      }

      setSelectedPeriodKey(periodKey);

      const periodsToLoad: string[] = [];

      for (let i = -PERIODS_BEFORE_AFTER; i <= PERIODS_BEFORE_AFTER; i++) {
        const checkPeriodKey = addPeriods(periodKey, i, groupBy);

        if (findPeriodIndex(allData, checkPeriodKey, groupBy) < 0) {
          periodsToLoad.push(checkPeriodKey);
        }
      }

      if (periodsToLoad.length === 0) {
        return;
      }

      setIsLoadingSelectedPeriod(true);

      try {
        const lowerCapDate = moment(billingStartDate);
        const currentPeriod = getPeriodKeyByOffset(0, groupBy);

        let lowerCapPeriod: string;

        switch (groupBy) {
          case 'WEEK':
            lowerCapPeriod = lowerCapDate.format('YYYY-[W]WW');
            break;
          case 'MONTH':
          default:
            lowerCapPeriod = lowerCapDate.format('YYYY-MM');
            break;
        }

        const validPeriodsToLoad = periodsToLoad.filter((p) => {
          const periodDate = getPeriodStart(p, groupBy);
          const lowerCapPeriodDate = getPeriodStart(lowerCapPeriod, groupBy);
          const currentPeriodDate = getPeriodStart(currentPeriod, groupBy);

          return (
            periodDate.isSameOrAfter(lowerCapPeriodDate) &&
            periodDate.isSameOrBefore(currentPeriodDate)
          );
        });

        const fetchPromises = validPeriodsToLoad.map(async (periodKey) => {
          try {
            const dateRange = getDateRangeForPeriodWithCurrentMonth(
              periodKey,
              groupBy,
            );

            const response = await SignalOccurrencesAPI.getSummary(companyId, {
              ...dateRange,
              groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
            });

            return (response.values || []).map((item) => ({
              item,
              periodKey,
            }));
          } catch (error) {
            logger.error('Error loading period', { periodKey, error });

            return [];
          }
        });

        const results = await Promise.all(fetchPromises);
        const allNewData = results.flat();

        if (allNewData.length > 0) {
          const { merged, periodKeys } = mergePeriodData(
            allData,
            allNewData,
            groupBy,
          );

          setAllData(merged);
          setLoadedPeriods(periodKeys);
        }
      } catch (error) {
        logger.error('Error loading selected period', { error });
      } finally {
        setIsLoadingSelectedPeriod(false);
      }
    },
    [
      allData,
      selectedPeriodKey,
      companyId,
      billingStartDate,
      groupBy,
      getDateRangeForPeriodWithCurrentMonth,
    ],
  );

  // Load earlier periods (scroll down)
  const loadEarlierPeriods = useCallback(async () => {
    if (isLoadingDown || allData.length === 0) {
      return;
    }

    const oldestItem = allData[allData.length - 1];
    const oldestPeriodKey = getPeriodKeyFromItem(oldestItem, groupBy);
    const oldestPeriodDate = getPeriodStart(oldestPeriodKey, groupBy);
    const lowerCapDate = moment(billingStartDate);

    let lowerCapPeriod: string;

    switch (groupBy) {
      case 'WEEK':
        lowerCapPeriod = lowerCapDate.format('YYYY-[W]WW');
        break;
      case 'MONTH':
      default:
        lowerCapPeriod = lowerCapDate.format('YYYY-MM');
        break;
    }

    const lowerCapPeriodDate = getPeriodStart(lowerCapPeriod, groupBy);

    if (oldestPeriodDate.isSameOrBefore(lowerCapPeriodDate)) {
      return;
    }

    const periodsToFetch: string[] = [];

    for (let i = 1; i <= ITEMS_PER_BATCH; i++) {
      const candidatePeriodKey = addPeriods(oldestPeriodKey, -i, groupBy);
      const candidatePeriodDate = getPeriodStart(candidatePeriodKey, groupBy);

      if (candidatePeriodDate.isBefore(lowerCapPeriodDate)) {
        break;
      }

      if (candidatePeriodDate.isSame(lowerCapPeriodDate)) {
        if (
          !loadedPeriods.has(candidatePeriodKey) &&
          !fetchingPeriodsRef.current.has(candidatePeriodKey)
        ) {
          periodsToFetch.push(candidatePeriodKey);
          fetchingPeriodsRef.current.add(candidatePeriodKey);
        }
        break;
      }

      if (
        loadedPeriods.has(candidatePeriodKey) ||
        fetchingPeriodsRef.current.has(candidatePeriodKey)
      ) {
        continue;
      }

      periodsToFetch.push(candidatePeriodKey);
      fetchingPeriodsRef.current.add(candidatePeriodKey);
    }

    if (periodsToFetch.length === 0) {
      return;
    }

    setIsLoadingDown(true);

    try {
      const fetchPromises = periodsToFetch.map(async (periodKey) => {
        try {
          const dateRange = getDateRangeForPeriodWithCurrentMonth(
            periodKey,
            groupBy,
          );

          const response = await SignalOccurrencesAPI.getSummary(companyId, {
            ...dateRange,
            groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
          });

          return (response.values || []).map((item) => ({
            item,
            periodKey,
          }));
        } catch (error) {
          logger.error('Error loading earlier period', { periodKey, error });

          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      const allNewData = results.flat();

      if (allNewData.length > 0) {
        const { merged, periodKeys } = mergePeriodData(
          allData,
          allNewData,
          groupBy,
        );

        setAllData(merged);
        setLoadedPeriods(periodKeys);
      }
    } catch (error) {
      logger.error('Error loading earlier periods', { error });
    } finally {
      setIsLoadingDown(false);
      periodsToFetch.forEach((key) => fetchingPeriodsRef.current.delete(key));
    }
  }, [
    allData,
    isLoadingDown,
    loadedPeriods,
    companyId,
    billingStartDate,
    groupBy,
    getDateRangeForPeriodWithCurrentMonth,
  ]);

  // Load later periods (scroll up)
  const loadLaterPeriods = useCallback(async () => {
    if (isLoadingUp || allData.length === 0) {
      return;
    }

    const newestItem = allData[0];
    const newestPeriodKey = getPeriodKeyFromItem(newestItem, groupBy);
    const newestPeriodDate = getPeriodStart(newestPeriodKey, groupBy);
    const currentPeriod = getPeriodKeyByOffset(0, groupBy);
    const currentPeriodDate = getPeriodStart(currentPeriod, groupBy);

    if (newestPeriodDate.isSameOrAfter(currentPeriodDate)) {
      return;
    }

    const periodsToFetch: string[] = [];

    for (let i = 1; i <= ITEMS_PER_BATCH; i++) {
      const candidatePeriodKey = addPeriods(newestPeriodKey, i, groupBy);
      const candidatePeriodDate = getPeriodStart(candidatePeriodKey, groupBy);

      if (candidatePeriodDate.isAfter(currentPeriodDate)) {
        break;
      }

      if (
        loadedPeriods.has(candidatePeriodKey) ||
        fetchingPeriodsRef.current.has(candidatePeriodKey)
      ) {
        continue;
      }

      periodsToFetch.push(candidatePeriodKey);
      fetchingPeriodsRef.current.add(candidatePeriodKey);
    }

    if (periodsToFetch.length === 0) {
      return;
    }

    setIsLoadingUp(true);

    try {
      const fetchPromises = periodsToFetch.map(async (periodKey) => {
        try {
          const dateRange = getDateRangeForPeriodWithCurrentMonth(
            periodKey,
            groupBy,
          );

          const response = await SignalOccurrencesAPI.getSummary(companyId, {
            ...dateRange,
            groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
          });

          return (response.values || []).map((item) => ({
            item,
            periodKey,
          }));
        } catch (error) {
          logger.error('Error loading later period', { periodKey, error });

          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      const allNewData = results.flat();

      if (allNewData.length > 0) {
        const { merged, periodKeys } = mergePeriodData(
          allData,
          allNewData,
          groupBy,
        );

        setAllData(merged);
        setLoadedPeriods(periodKeys);
      }
    } catch (error) {
      logger.error('Error loading later periods', { error });
    } finally {
      setIsLoadingUp(false);
      periodsToFetch.forEach((key) => fetchingPeriodsRef.current.delete(key));
    }
  }, [
    allData,
    isLoadingUp,
    loadedPeriods,
    companyId,
    groupBy,
    getDateRangeForPeriodWithCurrentMonth,
  ]);

  return {
    data: allData,
    selectedPeriodKey,
    isInitializing,
    isLoadingSelectedPeriod,
    isLoadingDown,
    isLoadingUp,
    handlePeriodSelect,
    loadEarlierPeriods,
    loadLaterPeriods,
  };
};
